// libs
import React, { memo, useState, useEffect, useRef } from "react";
import { css, keyframes } from "@emotion/core";

// components
import WithStyles from "shared/components/WithStyles";
import WhatsNew from "./components/WhatsNew";
import Brands from "./components/Brands";
import SubMenu from "./components/SubMenu";

// global hooks
import useTouchOut from "shared/hooks/useTouchOut";

// styles
import { DWEB_HEADER_HEIGHT, DWEB_DISCOUNT_HEIGHT } from "shared/styles/base";

// constants
import { MENU_TAGS } from "shared/components/Header/constants";

const fadeIn = keyframes`
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
    }

    80% {
        opacity: 0.5;
    }

    to {
        opacity: 0;
    }
`;

const catCss = css`
    color: #666;
    text-transform: uppercase;
    cursor: pointer;

    &:hover,
    &.hovered {
        color: #000;

        > .c-title {
            position: relative;

            &:after {
                position: absolute;
                content: "";
                height: 2px;
                bottom: -2px;
                left: 0;
                right: 0;
                background: #000;
            }
        }
    }

    .subMenu {
        position: absolute;
        top: ${DWEB_HEADER_HEIGHT + DWEB_DISCOUNT_HEIGHT}px;
        left: 0;
        width: 100%;
        background: #fff;
        border-top: 1px solid #999;
        box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.14);
        color: #000;
        cursor: default;
        display: none;
    }

    .subMenu-discount {
        top: ${DWEB_HEADER_HEIGHT}px;
    }

    .subMenu-visible {
        display: block;
    }

    .subMenu-show {
        animation: ${fadeIn} 0.25s ease-in;
        z-index: 3;
    }

    .subMenu-hide {
        animation: ${fadeOut} 0.25s ease-out forwards;
        z-index: 1 !important;
    }
`;

const getSubMenu = (data: any) => {
    switch (data.tag) {
        case MENU_TAGS.WHATS_NEW: {
            return <WhatsNew data={data} />;
        }

        case MENU_TAGS.BRANDS: {
            return <Brands data={data} />;
        }

        default: {
            return <SubMenu data={data} />;
        }
    }
};

interface ICategoryProps {
    data: any;
    onMouseIn: (id: number, immediate?: boolean) => void;
    onMouseOut: (immediate?: boolean) => void;
    showSubCategory: boolean;
    hasHeaderDiscount: boolean;
}

function Category({
    data,
    onMouseIn,
    onMouseOut,
    showSubCategory,
    hasHeaderDiscount,
}: ICategoryProps) {
    const [renderSub, setRenderSub] = useState(false);
    const [showSub, setShowSub] = useState<boolean>(showSubCategory);
    const [hideSub, setHideSub] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (renderSub === false) {
            setRenderSub(true);
        }
    }, [renderSub]);

    useEffect(() => {
        if (showSubCategory === true) {
            setShowSub(true);
            setHideSub(false);
        } else {
            setHideSub(true);
        }
    }, [showSubCategory]);

    useTouchOut({
        allowTouchCapture: showSub,
        onTouchOut: () => {
            onMouseOut(true);
        },
        parentRef: containerRef,
    });

    const onAnimationEnd = () => {
        if (hideSub === false) {
            return;
        }

        setShowSub(false);
        setHideSub(false);
    };

    const subChild = [
        "subMenu",
        hideSub && "subMenu-hide",
        hideSub === false && "subMenu-show",
        showSub && "subMenu-visible",
        hasHeaderDiscount && "subMenu-discount",
    ]
        .filter(Boolean)
        .join(" ");

    const catCls = showSub ? "hovered" : "";

    return (
        <WithStyles
            cssProps={catCss}
            className={catCls}
            onMouseEnter={() => onMouseIn(data.id)}
            onMouseLeave={() => onMouseOut()}
            onFocus={() => onMouseIn(data.id, true)}
            elRef={containerRef}
        >
            <div className="c-title">{data.title}</div>
            {renderSub && (
                <div className={subChild} onAnimationEnd={onAnimationEnd}>
                    {getSubMenu(data)}
                </div>
            )}
        </WithStyles>
    );
}

export default memo(Category);
